import { BRAIN, STATE } from '../actors/ai.js';

/**
 * @file THE ESCORT. A follower is a fighter with a HOME THAT MOVES.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THERE IS NO `BRAIN.FOLLOW`
 * ─────────────────────────────────────────────────────────────────────────────
 * The behaviour a teammate needs — walk to a position, hold there without
 * wandering, break off to fight something that comes close, come back when the
 * fight drifts too far — is EXACTLY {@link import('../actors/ai.js').stepHomeFSM}
 * plus {@link import('../actors/ai.js').leashCheck}, which have been in
 * `EnemyManager` since squad mode and are pinned by `tests/hero.test.mjs`
 * sections (b)–(g). Squad mode wrote a home ONCE, at formation build; this file
 * writes it EVERY STEP, at an offset from the player. That one difference is
 * the whole escort feature, and it needed not a single new byte in the manager.
 *
 * So a follower is: `team` = an ally faction, `brain` = `NONE` (an ordinary
 * fighter), `hasHome` = 1. Target acquisition, hostility, separation, the
 * ranged-fire path and the death animation are all the code that was already
 * there — and because the faction matrix already says who a crew fights, a
 * teammate opens fire on a rival crew with no script at all.
 *
 * ⚠ It follows THE PLAYER'S HEADING, not the camera's. The formation is two
 * flank positions half a step behind a sticky heading taken from the player's
 * velocity (the camera yaw is free and manual in this game — see
 * `ui/hud/Minimap.jsx` — so a camera-relative formation would swing the escort
 * around the player every time you turned the view, which reads as panic).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * FOUR THINGS THAT WILL BITE
 * ─────────────────────────────────────────────────────────────────────────────
 * • **THE LEASH IS THE MANAGER'S, AND IT IS GLOBAL.** `AiTuning.leash` (11 m)
 *   is what decides how far a teammate will chase before walking back to you.
 *   It is not per-slot and must not become per-slot for this feature: 11 m
 *   around a MOVING home already reads correctly ("he fights what is near me"),
 *   and the number is shared with anything else that ever holds a position.
 *
 * • **A FOLLOWER IN A CAR IS `BRAIN.SEATED`, AND THE PLAYER'S CAR IS NOT A
 *   TRAFFIC SLOT.** `Traffic#getSeatWorld` serves DRIVING slots only; the
 *   player's car is `systems/Driving.js`'s body plus a PARKED slot handle. So
 *   the seat anchor is derived here from `traffic.seatLocal(pArch[carSlot], …)`
 *   and the driving body's transform — the same spec-derived local point every
 *   other seat in the game uses, put through the same `Ry(yaw)` mapping.
 *   Seat 0 is the player, so followers take 1 (front passenger) and, on a 2-row
 *   body only, 2 and 3.
 *
 * • **BOARDING IS A RANGE TEST AT THE MOMENT THE PLAYER GETS IN, NOT A RIDE
 *   REQUEST.** A follower more than {@link FOLLOW.boardDist} from the car when
 *   the player takes it is LEFT BEHIND, which is the honest reading — he was
 *   nowhere near the door. He is not stranded, because of the next point.
 *
 *   ⚠ IT WAS TRIED THE OTHER WAY AND IT IS A CLOSED NEGATIVE RESULT. A
 *   "standing offer while the car is slow" rule — any follower within range
 *   takes a free seat whenever the car is under 3 m/s — reads well on paper and
 *   played WORSE: crew climbing in and out at every junction, at every kerb,
 *   and whenever a fight slowed the car down, because a per-step offer fires
 *   again the instant its own conditions come back. The owner called it buggier
 *   than the edge it replaced. Do not re-derive it. The case it was reaching
 *   for — a crew spawning beside a player who is ALREADY driving — is solved at
 *   the source instead: `systems/Missions.js` puts the player out of the car
 *   before a briefing, so nobody is ever recruited through a windscreen.
 *
 * • **A FOLLOWER THAT FALLS FAR ENOUGH BEHIND IS WARPED, and that is not a
 *   cheat, it is the only thing that works.** The player drives at 17 m/s and a
 *   gang member runs at 3.2; without a catch-up the escort is a mission that
 *   fails itself. The warp only fires past {@link FOLLOW.warpDist} — well
 *   outside the ~38 m the camera can see (see CLAUDE.md's culling note) — so it
 *   is never on screen, and it lands the follower BEHIND the player, resolved
 *   against the collision grid, so he walks back into frame rather than
 *   appearing in it.
 *
 * • **...AND SO IS ONE THAT IS SIMPLY STUCK, whatever the distance.** A fighter
 *   has no pathfinding: it steers at its home and slides along whatever it
 *   hits, so a building between it and the player is a wall it grinds into
 *   forever. Watched happen — one of two escorts pinned at 36 m, sprinting,
 *   closing nothing, and outside no threshold that would have rescued it. See
 *   {@link FOLLOW.stuckAt}: no progress for `stuckTime` seconds past `stuckAt`
 *   metres is treated exactly like being lost.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NO THREE.JS
 * ─────────────────────────────────────────────────────────────────────────────
 * Manager, traffic and driving are injected, exactly as in `Population.js` and
 * `Carjack.js`, so `tests/missions.test.mjs` drives the formation, the boarding
 * and the warp in node.
 *
 * @module engine/systems/Followers
 */

/** @typedef {import('../actors/EnemyManager.js').EnemyManager} EnemyManager */

const TAU = Math.PI * 2;

/** Everything about how an escort holds station. */
export const FOLLOW = Object.freeze({
  /** Metres from the player the formation ring sits at. */
  ring: 2.8,
  /**
   * Half-angle of the rear arc the slots are spread across, radians. 0.62 rad
   * (~36°) puts two followers on the player's quarters rather than in single
   * file, which is what reads as an escort at a 52-degree pitch.
   */
  arc: 0.62,
  /** Below this speed² the follow heading holds — same sticky rule the map
   *  chevron uses, and for the same reason: a formation must not spin when you
   *  stop walking. */
  moveEps: 0.04,
  /**
   * ⚠ THE SPRINT, AND IT IS NOT A NICETY. A gang member's authored speed is
   * 3.2 m/s and the player's is 6.5 — so at 1x an escort CAN NEVER HOLD STATION
   * while you walk, let alone run. Measured before this existed: two followers
   * a steady 13.7 m behind a player crossing a block, i.e. out of the formation
   * and permanently on the warp's doorstep.
   *
   * So past `sprintAt` metres from its station a follower runs at
   * `sprintMul` × its authored speed — 3.2 becomes 8.3, comfortably over the
   * player's 6.5 — and inside it drops back to 1x, so a teammate walking beside
   * you walks.
   *
   * ⚠ The threshold IS the steady-state trail. A follower can only exceed the
   * player's speed while it is sprinting, so while you sprint it settles a
   * little over `sprintAt` from its station (~6 m behind you) and closes to the
   * ring the moment you stop. That is the behaviour, not a tuning failure:
   * 2.6 is what makes the closing rate 1.8 m/s rather than a token 0.5, so
   * "they caught up" is something you actually see happen.
   *
   * It rides `EnemyManager#setSpeedMul`, the same per-slot scale a chase target
   * uses. It is CLEARED on the way out ({@link remove}/{@link release}/
   * {@link dispose}) or a released ally sprints around the city forever.
   *
   * ─────────────────────────────────────────────────────────────────────────
   * ⚠⚠ THE PLAYER LEARNED TO SPRINT AND THIS NUMBER DID NOT MOVE
   * ─────────────────────────────────────────────────────────────────────────
   * `systems/Stamina.js` gave the player a 1.4x run, and the obvious reading is
   * that it broke this constant: 3.2 × 2.6 = **8.32 m/s** against a sprinting
   * player's 6.5 × 1.4 = **9.1**, a closing rate of **−0.78 m/s**, i.e. an
   * escort that can never catch up. That reading was reached, acted on, and was
   * WRONG — 2.6 was raised to 3.4 on it and the review scene caught it. This
   * section exists because the next reader will run the same arithmetic.
   *
   * **The player cannot sustain 9.1 m/s.** The bar is BOUNDED, so over any long
   * stretch what is spent equals what is regained and the sprint duty is fixed
   * at `sprintTime / (sprintTime + refillTime)` = 6/15 = **40%** — arithmetic,
   * not policy, and `Stamina.js`'s own header derives it. The player's long-run
   * mean is therefore `6.5 × (1 + 0.4 × 0.4)` = `6.5 × 1.16` = **7.54 m/s**,
   * and the escort's 8.32 beats it by **+0.78 m/s**.
   *
   * So the instantaneous deficit is REAL and EXPECTED, and it is the paragraph
   * above generalising unchanged: the formation BREATHES. A full 6 s burst puts
   * the escort `0.78 × 6` = **4.7 m** further back than the walking trail, and
   * it takes that back at 1.82 m/s in **2.6 s** of the 9 s refill. 4.7 m is
   * nowhere near {@link FOLLOW.warpDist} 55, and it is comfortably inside
   * {@link FOLLOW.stuckAt} 25 — where the stuck watchdog is switched off
   * entirely — so neither rescue can fire and nothing needs one.
   *
   * ⚠ **THE REAL CONSTRAINT IS A CEILING ON `STAMINA.mul`, AND NOTHING ELSE IN
   * THE TREE EXPRESSES IT.** What must hold is the escort against the SUSTAINED
   * mean, not against the burst:
   * ```
   *   3.2 * sprintMul   >  6.5 * (1 + duty * (mul - 1))      duty = 0.4
   *   8.32 / 6.5 = 1.28 >  1 + 0.4 * (mul - 1)
   *   0.28 / 0.4 = 0.7  >  mul - 1
   *                mul  <  1.7
   * ```
   * At the shipped 1.4 there is ~21% headroom. Past **1.7** an escort can never
   * regain station no matter how long the player walks — the permanent trail out
   * to `warpDist`, the failure wrongly claimed above as already happening. RAISE
   * `STAMINA.mul` PAST 1.7 AND THIS CONSTANT MUST MOVE WITH IT; below it, leave
   * it alone.
   *
   * ⚠ **3.4 IS A CLOSED ALTERNATIVE.** It buys +1.78 m/s against the burst and
   * costs the closing rate against a WALKING player — the one case that was
   * actually measured in a browser — **1.82 → 4.38 m/s**, a 2.4x change to a
   * playtested number spent on a case that self-corrects in 2.6 s. And a 10.88
   * m/s gang member crosses the 3.5 m `sprintAt` threshold almost instantly,
   * which is a yo-yo between 1x and 3.4x rather than an escort. Changing a
   * playtested constant on un-playtested arithmetic is the thing this codebase
   * warns about everywhere; it was done here once already.
   *
   * ⚠ The 1.4 and the 40% are MIRRORED CONSTANTS, quoted rather than imported,
   * and that is the deal `links.js` has with `countryroads.js`'s `JOINT_LEG`:
   * this module imports `ai.js` and nothing else — manager, traffic, driving and
   * collision are all injected, which is what keeps the formation, the boarding
   * and the warp provable in node (`tests/missions.test.mjs`). Reaching into
   * `systems/Stamina.js` for two numbers would spend that posture. The mirror is
   * STATED instead: move `STAMINA.mul` or the two durations, re-run the solve.
   */
  sprintAt: 3.5,
  sprintMul: 2.6,
  /** Metres past which a follower is teleported back behind the player. */
  warpDist: 55,
  /** ...and how far behind them it lands. */
  warpBack: 7,
  /** Seconds between warp attempts for one follower — a warp into a wall must
   *  not retry 60 times a second. */
  warpCooldown: 1.5,

  /**
   * ⚠ THE STUCK WARP, AND IT CLOSES A DEAD ZONE THE FIRST CUT LEFT OPEN.
   * A fighter has NO PATHFINDING — it steers straight at its home and slides
   * along whatever it hits (`EnemyManager`'s `resolveCircle`), which is right
   * for a street brawl and useless for walking round a block. So a follower
   * that ends up with a building between it and the player grinds into the wall
   * forever: too far to reach its station, too close to trip `warpDist`.
   * Measured in the browser — one of two escorts pinned at 36.4 m, sprinting at
   * 2.6x, closing exactly 0.0 m over three seconds, and never recovering.
   *
   * So progress is watched: `stuckTime` seconds without closing `stuckEps`
   * metres, while further than `stuckAt` from the player, and it warps like any
   * other lost follower.
   *
   * `stuckAt` is 25 m ON PURPOSE. At the game's 34 m camera distance and 52°
   * pitch the player can see roughly 25 m behind themselves (the same number
   * `WANTED.footRingMin` is pushed out past), so a warp beyond it is a warp
   * nobody watches happen. Lower would fix more cases and be visible; higher
   * would leave the dead zone open again.
   */
  stuckAt: 25,
  stuckTime: 3,
  stuckEps: 1,
  /** Metres from the car, at the moment the player takes it, to get a seat. */
  boardDist: 12,
  /** Metres out from the door a follower lands when the player gets out. */
  exitOut: 1.5,
});

/**
 * @param {Object} o
 * @param {EnemyManager} o.manager
 * @param {{position: {x: number, y: number, z: number}, velocity: {x: number, z: number}}} o.player
 * @param {any} [o.traffic] A `Traffic` director. Without one nobody ever rides.
 * @param {any} [o.driving] `createDriving`'s system. Same.
 * @param {any} [o.collision] `CollisionGrid` — warps and door drops resolve.
 * @param {(x: number, z: number) => number} [o.heightAt]
 * @param {import('../types.js').Bounds2|null} [o.bounds]
 * @param {Partial<typeof FOLLOW>} [o.tune]
 * @returns {Object}
 */
export function createFollowers(o) {
  const {
    manager,
    player: player0,
    traffic = null,
    driving = null,
    collision = null,
    heightAt = () => 0,
    bounds = null,
    tune = null,
  } = o ?? {};

  if (!manager) throw new Error('createFollowers: a manager is required');
  if (!player0) throw new Error('createFollowers: a player is required');

  const F = tune ? { ...FOLLOW, ...tune } : FOLLOW;
  /** Re-pointable: `Gameplay` rebuilds the `Player` when its sprite decodes. */
  let player = player0;

  /**
   * @typedef {Object} Follower
   * @property {number} slot Manager slot.
   * @property {number} team Faction it spawned with — the re-validation key, the
   *   same trick `Wanted.js#ours` uses against slot recycling.
   * @property {number} seat Seat it is riding in, or -1 on foot.
   * @property {number} warpT Seconds since the last warp attempt.
   * @property {number} stuckT Seconds without closing on the player.
   * @property {number} bestD Closest it has been to the player since `stuckT`
   *   was last reset — the progress watermark the stuck test measures against.
   * @property {string} tag Free-form owner label (a mission id), so one director
   *   can hold several escorts and release them separately.
   */
  /** @type {Follower[]} */
  const list = [];

  /** Sticky follow heading — see {@link FOLLOW.moveEps}. */
  let heading = 0;
  let disposed = false;
  /** Were we in a car last step? The boarding edge, not a poll. */
  let wasDriving = false;
  const probe = { x: 0, z: 0 };
  const local = { x: 0, z: 0 };

  // ── validity ───────────────────────────────────────────────────────────────

  /**
   * Is this still OUR follower? A manager slot frees on death and the ambient
   * director can refill it inside one step, so a bare index is a stranger
   * waiting to be given orders.
   * @param {Follower} f
   */
  const ours = (f) => f.slot >= 0 && manager.isLive(f.slot) && manager.team[f.slot] === f.team;

  /** @param {Follower} f @returns {boolean} Alive and not mid-death-shrink. */
  const fighting = (f) => ours(f) && manager.state[f.slot] !== STATE.DYING;

  /**
   * Pushes a point out of the world, then out of the level bounds.
   * @param {{x: number, z: number}} pt @param {number} r
   */
  const resolve = (pt, r) => {
    if (bounds) {
      pt.x = Math.min(Math.max(pt.x, bounds.minX + r), bounds.maxX - r);
      pt.z = Math.min(Math.max(pt.z, bounds.minZ + r), bounds.maxZ - r);
    }
    if (!collision || typeof collision.resolveCircle !== 'function') return;
    probe.x = pt.x;
    probe.z = pt.z;
    const res = collision.resolveCircle(probe, r, 2);
    pt.x = res.x;
    pt.z = res.z;
  };

  // ── seats (the player's car is not a traffic slot — see the header) ─────────

  /**
   * How many PASSENGER seats the player's current car has. Seat 0 is the
   * player, so this is `seatCount - 1`: 1 on a pickup, 3 on a 2-row body.
   * @returns {number}
   */
  const passengerSeats = () => {
    const car = driving?.carSlot ?? -1;
    if (!traffic || car < 0) return 0;
    const arch = traffic.pArch?.[car];
    if (arch === undefined) return 0;
    return Math.max(0, (traffic.seatCount?.(arch) ?? 2) - 1);
  };

  /**
   * The world transform of a seat in the PLAYER'S car. Same spec-derived local
   * point and the same `Ry(yaw)` mapping as `Traffic#getSeatWorld`; only the
   * body it is measured from is different.
   *
   * @param {number} seat 1 = front passenger, 2/3 = rear pair.
   * @param {{x: number, y: number, z: number, yaw: number}} out REUSED.
   * @returns {{x: number, y: number, z: number, yaw: number}|null}
   */
  const seatWorld = (seat, out) => {
    const car = driving?.carSlot ?? -1;
    if (!traffic || car < 0 || !driving?.inCar) return null;
    const arch = traffic.pArch?.[car];
    if (arch === undefined) return null;
    const l = traffic.seatLocal(arch, seat, local);
    const b = driving.body;
    const c = Math.cos(b.yaw);
    const s = Math.sin(b.yaw);
    out.x = b.x + l.x * c + l.z * s;
    out.z = b.z - l.x * s + l.z * c;
    out.y = b.y + 0.14;
    // The occupant's facing is the CAR's heading in the ACTOR convention
    // (`atan2(dx, dz)`), not the vehicle one — identical to `getSeatWorld`.
    out.yaw = Math.atan2(c, -s);
    return out;
  };

  const seatOut = { x: 0, y: 0, z: 0, yaw: 0 };

  /**
   * Puts a follower in the player's car: inert, slaved, and NOT DRAWN (a car is
   * drawn empty — `EnemyManager#inCabin`, owner decision, playtest round 2).
   * @param {Follower} f @param {number} seat
   */
  const board = (f, seat) => {
    const w = seatWorld(seat, seatOut);
    if (!w) return false;
    f.seat = seat;
    manager.setSpeedMul(f.slot, 1);
    manager.clearHome(f.slot);
    manager.setBrain(f.slot, BRAIN.SEATED);
    manager.placeSeated(f.slot, w.x, w.y, w.z, w.yaw);
    return true;
  };

  /**
   * ...and out again, beside the door it rode behind. A rear passenger uses the
   * rear of the same flank, which is close enough at this scale and much
   * cheaper than a fourth door anchor nobody can see.
   * @param {Follower} f
   */
  const alight = (f) => {
    const seat = f.seat;
    f.seat = -1;
    if (!ours(f)) return;
    // A rider was frozen mid-sprint; landing on the pavement at `sprintMul`
    // would have him overshoot his own station on the first step. (Named, not
    // numbered: the multiplier is derived and has already moved once.)
    manager.setSpeedMul(f.slot, 1);
    const b = driving?.body;
    if (b && traffic) {
      // Seat 1 and 3 are the passenger flank (+Z local), 2 is the driver's.
      const side = seat === 2 ? -1 : 1;
      const r = manager.radius[f.slot] ?? 0.4;
      const c = Math.cos(b.yaw);
      const s = Math.sin(b.yaw);
      const half = (b.chassis?.W ?? 1.8) / 2 + F.exitOut;
      const back = seat >= 2 ? -(b.chassis?.L ?? 4.7) * 0.18 : 0;
      probe.x = b.x + back * c + side * half * s;
      probe.z = b.z - back * s + side * half * c;
      resolve(probe, r);
      manager.x[f.slot] = probe.x;
      manager.z[f.slot] = probe.z;
      manager.y[f.slot] = heightAt(probe.x, probe.z);
    }
    manager.setBrain(f.slot, BRAIN.NONE);
  };

  // ── the formation ──────────────────────────────────────────────────────────

  /**
   * Where follower `k` of `n` should be standing, in world space. A rear arc
   * about the sticky heading: one follower falls in directly behind, two take
   * the quarters, three fan out across `2 * FOLLOW.arc`.
   *
   * @param {number} k @param {number} n
   * @param {{x: number, z: number}} out REUSED.
   */
  const stationOf = (k, n, out) => {
    // The arc is measured from DIRECTLY BEHIND (heading + PI) so the middle of
    // an odd-sized formation is always in the player's wake.
    const t = n <= 1 ? 0 : (k / (n - 1)) * 2 - 1;
    const a = heading + Math.PI + t * F.arc;
    out.x = player.position.x + Math.sin(a) * F.ring;
    out.z = player.position.z + Math.cos(a) * F.ring;
    return out;
  };

  const station = { x: 0, z: 0 };

  /**
   * ONE follower handed back to the world: out of the car if it was in one, off
   * the leash, and off the sprint. Every exit path funnels through here, which
   * is what stops a released ally from jogging round the city at `sprintMul`
   * forever.
   * @param {Follower} f
   */
  const release1 = (f) => {
    if (f.seat >= 0) alight(f);
    if (!ours(f)) return;
    manager.clearHome(f.slot);
    manager.setSpeedMul(f.slot, 1);
  };

  // ── the step ───────────────────────────────────────────────────────────────

  /**
   * One fixed step. Call AFTER the manager (so the transforms a renderer reads
   * are this frame's) and after `Driving`, so a follower boards the car the
   * player is actually in.
   * @param {number} dt
   */
  const update = (dt) => {
    if (disposed || !(dt > 0)) return;

    // 1. drop anything the world has taken back. A follower that DIED is still
    //    in the list for `tests`/`Missions` to notice; `onSlotFreed` is the
    //    definitive removal and it fires the instant the slot frees.
    for (let i = list.length - 1; i >= 0; i--) if (!ours(list[i])) list.splice(i, 1);
    if (!list.length) {
      wasDriving = !!driving?.inCar;
      return;
    }

    // 2. the sticky heading.
    const vx = player.velocity?.x ?? 0;
    const vz = player.velocity?.z ?? 0;
    if (vx * vx + vz * vz > F.moveEps) heading = Math.atan2(vx, vz);
    else if (driving?.inCar) heading = driving.body.yaw;

    // 3. the car edge. Boarding is decided ONCE, on the step the player takes a
    //    car — see the header. Alighting is the same edge the other way.
    const inCar = !!driving?.inCar;
    if (inCar && !wasDriving) {
      const seats = passengerSeats();
      const b = driving.body;
      const d2 = F.boardDist * F.boardDist;
      let seat = 1;
      for (const f of list) {
        if (seat > seats) break;
        if (!fighting(f)) continue;
        const dx = manager.x[f.slot] - b.x;
        const dz = manager.z[f.slot] - b.z;
        if (dx * dx + dz * dz > d2) continue;
        if (board(f, seat)) seat++;
      }
    } else if (!inCar && wasDriving) {
      for (const f of list) if (f.seat >= 0) alight(f);
    }
    wasDriving = inCar;

    // 4. hold station — or ride.
    const n = list.length;
    for (let k = 0; k < n; k++) {
      const f = list[k];
      if (f.seat >= 0) {
        // Riding: the seat is the whole transform. A car that stopped being the
        // player's under us (a bail, a teardown) drops the passenger out rather
        // than freezing him at the last seat anchor.
        const w = seatWorld(f.seat, seatOut);
        if (w) manager.placeSeated(f.slot, w.x, w.y, w.z, w.yaw);
        else alight(f);
        continue;
      }
      if (!fighting(f)) continue;

      stationOf(k, n, station);
      manager.setHome(f.slot, station.x, station.z);

      // 5. the sprint. Measured against the STATION rather than the player, so
      //    a follower on the far side of you from its slot still closes.
      const sx = manager.x[f.slot] - station.x;
      const sz = manager.z[f.slot] - station.z;
      manager.setSpeedMul(f.slot, sx * sx + sz * sz > F.sprintAt * F.sprintAt ? F.sprintMul : 1);

      // 6. the catch-up warp — LOST, or STUCK. See `FOLLOW.stuckAt` for why the
      //    second test has to exist at all.
      f.warpT += dt;
      const dx = manager.x[f.slot] - player.position.x;
      const dz = manager.z[f.slot] - player.position.z;
      const d2 = dx * dx + dz * dz;

      const d = Math.sqrt(d2);
      if (d < F.stuckAt || d < f.bestD - F.stuckEps) {
        // Close enough not to care, or genuinely making ground: reset.
        f.stuckT = 0;
        f.bestD = d;
      } else {
        f.stuckT += dt;
      }
      // ⚠ `bestD` IS THE DISTANCE AT THE LAST RESET AND NOTHING ELSE. The
      // obvious `if (d < bestD) bestD = d` in the else branch destroys the whole
      // test: the watermark then follows the actor down step by step, so
      // `d < bestD - stuckEps` can never hold and a follower closing steadily
      // is declared stuck. Together the two numbers say "close `stuckEps`
      // metres every `stuckTime` seconds or you are stuck", i.e. a minimum
      // tolerated closing rate of 0.33 m/s — a fifth of the 1.82 m/s a follower
      // walking round an obstacle at the `sprintMul` sprint manages against a
      // walking player, and still under the 0.78 it manages against a sprinting
      // one (see `sprintMul`: the deficit during a burst is entirely inside
      // `stuckAt`, where this test is switched off anyway).

      const lost = d2 > F.warpDist * F.warpDist;
      const stuck = f.stuckT >= F.stuckTime;
      if ((lost || stuck) && f.warpT >= F.warpCooldown) {
        f.warpT = 0;
        f.stuckT = 0;
        f.bestD = Infinity;
        const a = heading + Math.PI;
        probe.x = player.position.x + Math.sin(a) * F.warpBack;
        probe.z = player.position.z + Math.cos(a) * F.warpBack;
        resolve(probe, manager.radius[f.slot] ?? 0.4);
        manager.x[f.slot] = probe.x;
        manager.z[f.slot] = probe.z;
        manager.y[f.slot] = heightAt(probe.x, probe.z);
        manager.vx[f.slot] = 0;
        manager.vz[f.slot] = 0;
        manager.setHome(f.slot, probe.x, probe.z);
      } else if (f.seat < 0 && d <= F.stuckAt) {
        // Home again: nothing to watch until it drifts back out.
        f.bestD = Math.min(f.bestD, d);
      }
    }
  };

  return {
    name: 'followers',

    /**
     * Takes a live manager slot into the escort. The caller owns the spawn — a
     * follower is an ordinary actor, and which def and which faction it is are
     * mission content.
     *
     * @param {number} slot
     * @param {string} [tag] Owner label, so {@link release} can free one
     *   mission's escort without touching another's.
     * @returns {boolean}
     */
    add(slot, tag = '') {
      if (disposed || !manager.isLive(slot)) return false;
      if (list.some((f) => f.slot === slot)) return false;
      manager.setBrain(slot, BRAIN.NONE);
      manager.setHome(slot, manager.x[slot], manager.z[slot]);
      list.push({
        slot, team: manager.team[slot], seat: -1, warpT: F.warpCooldown, stuckT: 0, bestD: Infinity, tag,
      });
      return true;
    },

    /**
     * Drops a follower back into the world as an ordinary actor: no home, no
     * seat. It does NOT despawn — a mission that ends may want its survivors to
     * walk off, and that is the caller's decision.
     * @param {number} slot
     * @returns {boolean}
     */
    remove(slot) {
      const k = list.findIndex((f) => f.slot === slot);
      if (k < 0) return false;
      release1(list[k]);
      list.splice(k, 1);
      return true;
    },

    /**
     * Every follower carrying `tag`, released. @param {string} tag
     * @returns {number[]} The slots, so the caller can despawn them if it wants.
     */
    release(tag) {
      /** @type {number[]} */
      const out = [];
      for (let i = list.length - 1; i >= 0; i--) {
        if (list[i].tag !== tag) continue;
        out.push(list[i].slot);
        release1(list[i]);
        list.splice(i, 1);
      }
      return out;
    },

    /**
     * A manager slot went free, for ANY reason. Wired into
     * `EnemyManager#onDespawn` by `Gameplay` — the same door
     * `Population#onSlotFreed` uses, and the reason a recycled slot can never
     * inherit an escort's orders.
     * @param {number} slot
     */
    onSlotFreed(slot) {
      const k = list.findIndex((f) => f.slot === slot);
      if (k >= 0) list.splice(k, 1);
    },

    /** @param {number} slot @returns {boolean} */
    has(slot) {
      return list.some((f) => f.slot === slot);
    },

    /** @param {string} [tag] @returns {number[]} Live slots, in formation order. */
    slots(tag) {
      return list.filter((f) => (tag === undefined || f.tag === tag) && ours(f)).map((f) => f.slot);
    },

    /** @returns {number} */
    get count() {
      return list.length;
    },

    /** @returns {number} Followers currently riding in the player's car. */
    get riding() {
      let n = 0;
      for (const f of list) if (f.seat >= 0) n++;
      return n;
    },

    update,

    /** `Gameplay`'s player hot-swap points us at the replacement actor. */
    setPlayer(p) {
      if (p) player = p;
    },

    /** @returns {{count: number, riding: number, heading: number}} */
    stats() {
      return {
        count: list.length,
        riding: this.riding,
        heading: Math.round(((heading % TAU) + TAU) % TAU * 100) / 100,
      };
    },

    /**
     * Idempotent. Hands every follower back to the world as a loose actor —
     * never despawns one, for the same reason {@link remove} does not: whether
     * a teammate should vanish when the system goes away is the caller's call,
     * and `Missions.js` makes it explicitly.
     */
    dispose() {
      if (disposed) return;
      disposed = true;
      for (const f of list) release1(f);
      list.length = 0;
    },
  };
}
